import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { doc, serverTimestamp, updateDoc } from "firebase/firestore";
import { auth, db } from "../firebaseConfig";
import { useUser } from "../context/UserContext";
import {
  clearPendingNotificationRedirect,
  getPendingNotificationRedirect,
  isFreshPendingNotificationRedirect,
} from "../utils/authVerification";
import WarningModal from "../components/WarningModal";
import { toast } from "react-toastify";
import { ToastContainer } from "react-toastify";

const CURRENT_TERMS_VERSION = "2026-04";

export default function Splash() {
  const navigate = useNavigate();
  const { currentUser } = useUser();
  const [accepted, setAccepted] = useState(false);
  const [requiresAcceptance, setRequiresAcceptance] = useState(true);

  const sessionAcknowledged = useMemo(
    () => sessionStorage.getItem("sessionAcknowledged") === "true",
    []
  );

  useEffect(() => {
    if (sessionAcknowledged) {
      const pendingRedirect = getPendingNotificationRedirect(auth.currentUser?.uid);
      clearPendingNotificationRedirect();
      const nextRoute = isFreshPendingNotificationRedirect(pendingRedirect) ?
        pendingRedirect.route :
        "/messages";
      navigate(nextRoute, { replace: true });
    }
  }, [navigate, sessionAcknowledged]);

  useEffect(() => {
    const needsTermsAcceptance =
      currentUser?.termsVersion !== CURRENT_TERMS_VERSION ||
      currentUser?.termsAccepted !== true;
    setRequiresAcceptance(needsTermsAcceptance);
    setAccepted(!needsTermsAcceptance);
  }, [currentUser]);

  useEffect(() => {
    const message = sessionStorage.getItem("pendingWelcomeMessage");
    if (!message) return;
    sessionStorage.removeItem("pendingWelcomeMessage");
    toast.success(message);
  }, []);

  const handleAcknowledge = async () => {
    if (requiresAcceptance) {
      try {
        await updateDoc(doc(db, "users", auth.currentUser.uid), {
          termsAccepted: true,
          termsAcceptedAt: serverTimestamp(),
          termsVersion: CURRENT_TERMS_VERSION,
        });
        setRequiresAcceptance(false);
        setAccepted(true);
      } catch (err) {
        console.error("Terms acceptance failed:", err);
        toast.error("Unable to save terms acceptance. Please try again.");
        return;
      }
    }

    sessionStorage.setItem("sessionAcknowledged", "true");
    const pendingRedirect = getPendingNotificationRedirect(auth.currentUser?.uid);
    clearPendingNotificationRedirect();
    const nextRoute = isFreshPendingNotificationRedirect(pendingRedirect) ?
      pendingRedirect.route :
      "/messages";
    navigate(nextRoute, { replace: true });
  };

  return (
    <>
      <WarningModal
        accepted={accepted}
        onAcceptedChange={setAccepted}
        requiresAcceptance={requiresAcceptance}
        onAcknowledge={handleAcknowledge}
      />
      <ToastContainer />
    </>
  );
}
